import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

/**
 * Design-system primitives. See docs/DESIGN-SYSTEM.md.
 *
 * Deliberately small and unabstracted — a handful of components with explicit
 * variants, not a framework. Every control is 40px minimum and every focus
 * ring comes from the shared token, because the primary user works in this all
 * day and keyboard navigation is a first-class path.
 */

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

// --- Button -------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 disabled:bg-blue-300',
  secondary:
    'bg-surface text-body border border-border-input hover:bg-blue-50 disabled:text-subtle',
  ghost: 'text-blue-700 hover:bg-blue-50 disabled:text-subtle',
  danger: 'bg-danger text-white hover:opacity-90',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', icon, className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cx(
        'inline-flex h-control items-center justify-center gap-2 rounded-md px-4 text-body-sm font-medium transition-fast disabled:cursor-not-allowed',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
});

/** Square icon-only button. Always needs an aria-label. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string }
>(function IconButton({ label, className, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      className={cx(
        'inline-flex h-9 w-9 items-center justify-center rounded-md text-muted transition-fast hover:bg-blue-50 hover:text-blue-700',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

// --- Input --------------------------------------------------------------------

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, id, className, ...props },
  ref,
) {
  const inputId = id ?? `field-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div>
      {/* Label always visible. A placeholder disappears the moment typing
          starts, which is exactly when the user most needs to know the field. */}
      <label htmlFor={inputId} className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-meta font-medium text-ink">{label}</span>
        {hint ? <span className="text-micro text-muted">{hint}</span> : null}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${inputId}-error` : undefined}
        className={cx(
          'h-control w-full rounded-md bg-surface px-3 text-body-sm text-ink',
          error ? 'border-2 border-danger' : 'border border-border-input',
          className,
        )}
        {...props}
      />
      {error ? (
        <p
          id={`${inputId}-error`}
          className="mt-1.5 flex items-center gap-1.5 text-micro text-danger"
        >
          <span aria-hidden>⚠</span>
          {error}
        </p>
      ) : null}
    </div>
  );
});

// --- Page furniture -------------------------------------------------------------

/**
 * The heading every screen opens with.
 *
 * Extracted once the settings screens arrived and four pages started
 * reinventing the same title-description-actions row with slightly different
 * spacing each time.
 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-4">
      <div className="min-w-0">
        <h1 className="text-h1 text-ink">{title}</h1>
        {description ? (
          <p className="mt-1 max-w-2xl text-body-sm text-muted">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/** A bordered panel with an optional title. The unit settings pages are built from. */
export function Card({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('rounded-xl border border-border-strong bg-surface', className)}>
      {title ? (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-h3 text-ink">{title}</h2>
            {description ? <p className="mt-1 text-body-sm text-muted">{description}</p> : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

/** A short status word in a coloured pill. */
export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  children: ReactNode;
}) {
  const tones = {
    neutral: 'bg-sunken text-muted',
    success: 'bg-success-bg text-[var(--success)]',
    warning: 'bg-warning-bg text-[var(--warning)]',
    danger: 'bg-danger-bg text-[var(--danger)]',
    info: 'bg-blue-50 text-blue-700',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-micro font-medium',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

// --- Feedback -----------------------------------------------------------------

export function Alert({
  tone = 'danger',
  children,
}: {
  tone?: 'danger' | 'success' | 'warning';
  children: ReactNode;
}) {
  const tones = {
    danger: 'bg-danger-bg text-danger',
    success: 'bg-success-bg text-success',
    warning: 'bg-warning-bg text-warning',
  };
  return (
    <div role="alert" className={cx('rounded-md px-3 py-2.5 text-body-sm', tones[tone])}>
      {children}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span
      role="status"
      aria-label={label}
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"
    />
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border-strong bg-surface px-6 py-16 text-center">
      <h3 className="text-h3 text-ink">{title}</h3>
      <p className="mt-1.5 max-w-sm text-body-sm text-muted">{description}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

// --- Icons --------------------------------------------------------------------
// Inline SVG rather than an icon package: a dozen glyphs do not justify a
// dependency, and these inherit currentColor so they follow the design tokens.

/**
 * Icons live in their own module now — see icons.tsx. Re-exported here so the
 * existing `Icons.Foo` call sites keep working.
 */
export { Icons } from './icons.tsx';
